import { Component, ElementRef, type OnDestroy, type OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { type FormControlStatus, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import type { Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { EMAIL_PATTERN, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import {
  AnalyticsFormStatus,
  FormInputType,
  FormStatus,
  pnrFormatValidator,
  ReactiveFormStatus,
  softTicketNumberValidator,
  trackFormState,
} from '@fcom/service-forms';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonType,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import type { Attachment } from '@fcom/ui-components/components/interfaces';

import { SubmitService } from '../services/submit.service';
import { parsePhoneNumber, PHONE_NUMBER_PATTERN } from '../utils';

const CANCELLATION_FORM = 'cancellation-cover-claim';

@Component({
  selector: 'fin-cancellation-cover-claim-form',
  templateUrl: 'cancellation-cover-claim-form.component.html',
  styleUrls: ['./cancellation-cover-claim-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CancellationCoverClaimFormComponent implements OnInit, OnDestroy {
  readonly allowedFileTypes: FileUploaderComponent['allowedFileTypes'] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = 1;
  readonly maxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;

  readonly ButtonType = ButtonType;
  readonly FormInputType = FormInputType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;
  @ViewChild('formContainer', { static: true }) formContainer: ElementRef;

  formStatus: FormStatus = FormStatus.INITIAL;
  reactiveForm: UntypedFormGroup;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private scrollService: ScrollService,
    private store$: Store<AppState>,
    private submitService: SubmitService
  ) {}

  get attachment(): Attachment | undefined {
    return this.fileUploader?.attachments[0];
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1)).subscribe((profile: Profile): void => {
        this.createForm(profile);
        this.addFormWatchers();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(profile?: Profile): void {
    this.reactiveForm = this.fb.group({
      bookingReference: ['', [Validators.required, pnrFormatValidator()]],
      ticketNumber: ['', [Validators.required, softTicketNumberValidator()]],
      firstName: [profile?.firstname ?? '', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      familyName: [profile?.lastname ?? '', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      email: [profile?.email ?? '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      phoneNumber: [parsePhoneNumber(profile), [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]],
      refundFor: ['departureAndReturnFlights', [Validators.required]],
      file: [[], Validators.required],
      attachmentConsent: [false, [Validators.requiredTrue]],
    });
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(CANCELLATION_FORM, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);

    // Angular fills the form with original values if submit fails
    // but setting file input value to anything else than empty string throws an error
    const file: File = this.attachment?.file;
    this.reactiveForm.get('file').reset();
    this.subscriptions.add(
      this.submitService
        .sendCancellationCoverClaimRequest(this.reactiveForm.value, file)
        .pipe(take(1))
        .subscribe({
          next: (): void => {
            this.onSuccess();
          },
          error: (): void => {
            this.onError();
          },
        })
    );
  }

  private onSuccess(): void {
    this.formStatus = FormStatus.COMPLETE;
    trackFormState(CANCELLATION_FORM, AnalyticsFormStatus.SUCCESS, this.gtmService);
  }

  private onError(): void {
    this.formStatus = FormStatus.ERROR;
    trackFormState(CANCELLATION_FORM, AnalyticsFormStatus.ERROR, this.gtmService);
  }

  private addFormWatchers(): void {
    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status: FormControlStatus): boolean => status === ReactiveFormStatus.INVALID),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe((): void => {
          trackFormState(CANCELLATION_FORM, AnalyticsFormStatus.START, this.gtmService);
        })
    );
  }
}
